import { Component, OnInit, inject, signal, computed, effect, TemplateRef, ViewChild, ChangeDetectionStrategy, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import { RequestService, Request } from '../../services/requests.service';
import { AuthService } from '@services/core/auth.service';
import { PermissionService } from '@services/core/permission.service';
import { CatalogService } from '@services/data/catalog.service';
import { BpmService } from '../../services/bpm.service';
import { PeriodService } from '../../../management/billing-management/services/period.service';
import { ClaimInformePdfComponent } from 'app/feature/management-claim/components/claim-informe-pdf/claim-informe-pdf.component';
import { MembershipInformePdfComponent } from '../../components/membership-informe-pdf/membership-informe-pdf.component';
import { AdvancedFilterComponent, FilterField } from 'app/core/components/advanced-filter/advanced-filter.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';

const TERMINAL_FILTER = '__TERMINAL__';
/** Estados finales del BPM actual (más PAID/REIMBURSED legacy). */
const TERMINAL_STATUS_SLUGS = new Set([
  'APPROVED',
  'REJECTED',
  'DISCARDED',
  'PAID',
  'REIMBURSED',
  'EXPIRED'
]);
const BENEFIT_TYPE_LABELS: Record<string, string> = {
  alarm: 'Alarma',
  sensor: 'Sensor',
  gps: 'GPS',
  security_film: 'Lámina de seguridad',
  roof_rack: 'Portaequipajes',
  hands_free: 'Manos libres',
  glass_engraving: 'Grabado de vidrios',
  license: 'Licencia de conducir',
  notary: 'Gastos notariales',
  rental_car: 'Arriendo / Uber',
  tow_truck: 'Grúa',
  custody: 'Custodia'
};

@Component({
  selector: 'app-request-history',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, ClaimInformePdfComponent, MembershipInformePdfComponent, AdvancedFilterComponent, ListCardComponent, PageHeaderComponent, MobileFooterComponent],
  templateUrl: './request-history.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-history.component.scss']
})
export class RequestHistoryComponent implements OnInit {
  public appSettings = inject(AppSettings);
  private requestService = inject(RequestService);
  private catalogService = inject(CatalogService);
  readonly authService = inject(AuthService);
  readonly perm = inject(PermissionService);
  private bpmService = inject(BpmService);
  private periodService = inject(PeriodService);
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);

  allRequests = this.requestService.historyRequests;
  requestsLoading = this.requestService.isHistoryLoading;

  entities = this.catalogService.entities;
  processes = this.catalogService.processes;

  /** Entidades del rol activo para filtros (no el catálogo completo). */
  roleEntities = computed(() => {
    const assigned = (this.authService.sharingEntities() as any[]) || [];
    const catalog = this.catalogService.entities() || [];
    const byCode = new Map(catalog.map((e: any) => [Number(e.code), e]));
    return assigned
      .map((e: any) => {
        const code = Number(e.code ?? e.entityCode);
        if (!Number.isFinite(code)) return null;
        const cat = byCode.get(code);
        return { ...(cat || {}), ...e, code, name: cat?.name ?? e.name ?? String(code) };
      })
      .filter(Boolean) as any[];
  });

  searchQuery = signal('');
  selectedEntities = signal<any[]>([]);
  selectedProcess = signal<string | null>(null);
  selectedStatus = signal<string>(TERMINAL_FILTER);

  periods = signal<any[]>([]);
  selectedPeriodStartId = signal<number | null>(null);
  selectedPeriodEndId = signal<number | null>(null);
  filteredEndPeriods: any[] = [];
  periodFilterReady = signal(false);

  isGeneratingPdf = signal(false);
  pdfSrc: SafeResourceUrl | null = null;
  private pdfBlobUrl: string | null = null;

  @ViewChild(ClaimInformePdfComponent) claimInformePdf?: ClaimInformePdfComponent;
  @ViewChild(MembershipInformePdfComponent) membershipPdf?: MembershipInformePdfComponent;
  selectedMembershipRequest = signal<Request | null>(null);

  statusOptions = [
    { slug: TERMINAL_FILTER, name: 'Todas' },
    { slug: 'APPROVED', name: 'Aprobada' },
    { slug: 'REJECTED', name: 'Rechazada' },
    { slug: 'DISCARDED', name: 'Desechada' }
  ];

  isLoading = computed(() => this.requestsLoading() || !this.periodFilterReady());

  advancedFilterFields: FilterField[] = [
    { key: 'party', label: 'Socio / RUT' },
    { key: 'plate', label: 'Patente' },
    { key: 'folio', label: 'Folio / UUID' },
    { key: 'entityCode', label: 'Código Entidad' }
  ];
  activeAdvancedFilters = signal<Array<{ field: string; value: string }>>([]);

  /**
   * Historial = solo solicitudes que ya terminaron (Aprobada / Rechazada / Desechada).
   * Filtra por periodo (desde/hasta) para roles con mucho volumen.
   * Desechadas sin periodo de facturación: se incluyen por fecha de creación.
   */
  scopedRequests = computed(() => {
    let list = (this.allRequests() || []).filter(r => this.isTerminalRequest(r));
    list = this.filterBySearch(list);
    list = this.filterByEntities(list);
    list = this.filterByProcess(list);
    list = this.filterByPeriod(list);
    return list;
  });

  filteredRequests = computed(() => {
    const list = this.filterByStatus(this.scopedRequests());
    return this._applyAdvancedFilters(list);
  });

  // Comprueba coincidencia en campos de texto de la solicitud
  private _matchRequestField(r: Request, field: string, val: string): boolean {
    if (field === 'plate') {
      const plate = (r.vehicle?.plate ?? r.claim?.vehicle?.plate ?? r.benefit?.vehicle?.plate ?? '').toLowerCase();
      return plate.includes(val);
    }
    const target = field === 'folio' ? r.uuid : r.entityCode;
    return String(target ?? '').toLowerCase().includes(val);
  }

  // Evalúa si una solicitud cumple una regla del filtro avanzado
  private _matchAdvancedRule(r: Request, rule: { field: string; value: string }): boolean {
    const val = (rule?.value ?? '').toLowerCase();
    if (!val) return true;
    if (rule.field === 'party') {
      const partyStr = `${r.party?.names ?? ''} ${r.party?.lastNames ?? ''} ${r.party?.rut ?? ''}`.toLowerCase();
      return partyStr.includes(val);
    }
    return this._matchRequestField(r, rule.field, val);
  }

  // Aplica la lista de filtros avanzados activos
  private _applyAdvancedFilters(list: Request[]): Request[] {
    const filters = this.activeAdvancedFilters() ?? [];
    if (!filters.length) return list;
    return list.filter(r => filters.every(rule => this._matchAdvancedRule(r, rule)));
  }

  totalRequestsCount = computed(() => this.scopedRequests().length);

  approvedRequestsCount = computed(() => {
    return this.scopedRequests().filter(r => {
      const slug = String(r.statusSlug || '').toUpperCase();
      return slug === 'APPROVED' || slug === 'PAID' || slug === 'REIMBURSED';
    }).length;
  });

  rejectedRequestsCount = computed(() => {
    return this.scopedRequests().filter(r =>
      String(r.statusSlug || '').toUpperCase() === 'REJECTED'
    ).length;
  });

  discardedRequestsCount = computed(() => {
    return this.scopedRequests().filter(r => this.bpmService.isDiscarded(r)).length;
  });

  constructor() {
    effect(() => {
      const entity = this.authService.sharingEntity();
      const role = this.authService.sharingRole();
      if (this.authService.isUserRole(role) || (entity && entity['code'])) {
        this.loadRequests();
      }
    });
    effect(() => {
      const list = this.periodService.billingPeriods();
      const rev = this.periodService.revision();
      if (!rev && !list.length) return;
      untracked(() => this.applyPeriods(list));
    });
  }

  ngOnInit() {
    if (!this.periodService.periods().length) this.periodService.reload();
  }

  loadRequests() {
    this.requestService.loadHistoryRequests();
  }

  applyPeriods(processedPeriods: any[]): void {
    this.periods.set(processedPeriods);
    if (processedPeriods.length > 0) {
      const lastPeriod = processedPeriods[0];
      const yearStart = processedPeriods.find(p => p.year === lastPeriod.year && Number(p.period) === 1)
        ?? processedPeriods[processedPeriods.length - 1];
      this.selectedPeriodStartId.set(yearStart.id);
      this.selectedPeriodEndId.set(lastPeriod.id);
      this.updateFilteredEndPeriods();
    }
    this.periodFilterReady.set(true);
  }

  updateFilteredEndPeriods(): void {
    const startValue = Number(this.selectedPeriodStartId() ?? 0);
    this.filteredEndPeriods = this.periods().filter(p => Number(p.id) >= startValue);

    if (Number(this.selectedPeriodEndId() ?? 0) < startValue) {
      this.selectedPeriodEndId.set(this.selectedPeriodStartId());
    }
  }

  onPeriodStartChange(id: number): void {
    this.selectedPeriodStartId.set(id);
    this.updateFilteredEndPeriods();
  }

  onPeriodEndChange(id: number): void {
    this.selectedPeriodEndId.set(id);
  }

  isClaimProcess(slug: string | null | undefined): boolean {
    return this.bpmService.isClaimProcess(slug);
  }

  canViewInforme(req: Request): boolean {
    if (this.isClaimProcess(req.processSlug)) {
      return !!(req.claim?.uuid || (req.claim as any)?.id);
    }
    return true;
  }

  // Despacha la generacion de informe segun el tipo de proceso
  async openInforme(req: Request, previewTpl: TemplateRef<any>): Promise<void> {
    if (this.isGeneratingPdf()) return;
    if (this.isClaimProcess(req.processSlug)) {
      return this._openClaimInforme(req);
    }
    if (this._isMembershipProcess(req.processSlug)) {
      return this._openMembershipInforme(req);
    }
    if (this.bpmService.isUnsubscribeProcess(req.processSlug)) {
      return this._openBajaInforme(req, previewTpl);
    }
    this.openSimplePreview(req, previewTpl);
  }

  // Prepara y abre el informe oficial de siniestro
  private async _openClaimInforme(req: Request): Promise<void> {
    const claim = req.claim;
    if (!claim?.uuid && !(claim as any)?.id) {
      Swal.fire('Sin informe', 'Esta solicitud no tiene un siniestro asociado.', 'info');
      return;
    }
    const reimbursement = Array.isArray(req.reimbursements) ? req.reimbursements[0] : null;
    const ctx = { claim: { ...claim, request: (claim as any).request ?? req }, party: req.party ?? (claim as any).party, reimbursement, transferCover: !!reimbursement };
    this.isGeneratingPdf.set(true);
    try { await this.claimInformePdf?.openPreview(ctx); }
    finally { this.isGeneratingPdf.set(false); }
  }

  // Abre la ficha oficial de solicitud de incorporacion
  private _openMembershipInforme(req: Request): void {
    this.selectedMembershipRequest.set(req);
    setTimeout(() => this.membershipPdf?.openPreview(), 0);
  }

  // Verifica si el proceso corresponde a incorporacion de socio o vehiculo
  private _isMembershipProcess(slug: string | null | undefined): boolean {
    const s = String(slug ?? '').toUpperCase();
    return s === 'MEMBERSHIP_NEW' || s === 'VEHICLE_NEW' || s === 'INCORPORATION';
  }

  // Genera y presenta el informe oficial de baja de cobertura
  private _openBajaInforme(req: Request, template: TemplateRef<any>): void {
    const doc = this._buildBajaDoc(req);
    this._showPdfInModal(doc, template);
  }

  // Construye el documento jsPDF para la baja de cobertura
  private _buildBajaDoc(req: Request): jsPDF {
    const doc = new jsPDF('p', 'pt', 'letter');
    const width = doc.internal.pageSize.getWidth() - 35;
    const entity = this.getEntityName(req.entityCode ?? '');
    this._renderBajaTables(doc, req);
    const pages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      this._addDocHeader(doc, entity, 'SOLICITUD DE BAJA DE COBERTURA');
      this._addDocFooter(doc, width, i, pages);
    }
    return doc;
  }

  // Agrega las tablas de socio, vehiculo y motivo al documento
  private _renderBajaTables(doc: jsPDF, req: Request): void {
    autoTable(doc, {
      startY: 105,
      body: this._buildBajaBody(req),
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 5 },
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
  }

  // Genera las secciones y filas de datos para la baja
  private _buildBajaBody(req: Request): any[][] {
    const p = req.party;
    const v = req.vehicle;
    const statusName = req.statusObj?.name ?? req.statusSlug ?? 'PROCESANDO';
    const dateStr = req.createdAt ? new Date(req.createdAt).toLocaleDateString('es-CL') : 'N/A';
    return [
      [{ content: '1. IDENTIFICACIÓN DEL SOCIO / PROPIETARIO', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } }],
      ['RUT:', p?.rut ?? 'N/A', 'Tipo Socio:', p?.groupName ?? p?.groupSlug ?? 'Socio Afiliado'],
      ['Nombres:', { content: `${p?.names ?? ''} ${p?.lastNames ?? ''}`.trim() || 'N/A', colSpan: 3 }],
      ['Correo:', p?.mail ?? 'No registrado', 'Móvil:', p?.mobileNumber ?? 'No registrado'],
      ['Entidad:', { content: `${req.entityCode ?? ''} - ${this.getEntityName(req.entityCode ?? '')}`, colSpan: 3 }],
      [{ content: '2. IDENTIFICACIÓN DEL VEHÍCULO', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } }],
      ['Patente:', v?.plate ?? 'S/P', 'Año:', String(v?.year ?? 'N/A')],
      ['Marca / Modelo:', { content: `${v?.brand?.name ?? v?.brandName ?? ''} ${v?.model?.name ?? v?.modelName ?? ''}`.trim() || 'N/A', colSpan: 3 }],
      ['Color:', v?.colorName ?? v?.color ?? 'N/A', 'VIN / Motor:', v?.vin ?? v?.motorNumber ?? 'N/A'],
      [{ content: '3. MOTIVO Y FECHA DE TRÁMITE', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } }],
      ['Fecha Solicitud:', dateStr, 'Estado:', statusName],
      ['Motivo / Causal:', { content: (req as any)?.reason ?? this.getProcessName(req.processSlug), colSpan: 3, styles: { fontStyle: 'italic' } }],
      [{ content: 'Declaro conocer y estar dispuesto a respetar los reglamentos del Fondo de Protección Vehicular (FPV) y renuncio a toda acción civil en contra de este Fondo. Acepto, además, las decisiones que la comisión del Fondo P.V. determine en relación a esta solicitud de baja.', colSpan: 4, styles: { textColor: [108, 117, 125], fontSize: 8 } }]
    ];
  }

  // Agrega el encabezado institucional en cada pagina
  private _addDocHeader(doc: jsPDF, entity: string, title: string): void {
    autoTable(doc, {
      startY: 28, head: [
        [{ content: 'Fondo de Protección Vehicular', styles: { fontStyle: 'normal', fontSize: 10 } }],
        [{ content: entity, styles: { fontStyle: 'italic', fontSize: 9.5 } }]
      ],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 1, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });
    autoTable(doc, {
      startY: 72, head: [[{ content: title, styles: { fontStyle: 'bold', fontSize: 11 } }]],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 0.3, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });
  }

  // Agrega el pie de pagina numerado
  private _addDocFooter(doc: jsPDF, width: number, page: number, total: number): void {
    const today = new Date().toLocaleDateString('es-CL');
    autoTable(doc, {
      startY: 742, body: [['Impreso: ' + today, 'Sistema: FPV', 'Página ' + page + ' de ' + total]],
      theme: 'plain', bodyStyles: { fontSize: 7, cellPadding: 0.3, textColor: [0, 0, 0] },
      didDrawPage: () => { doc.setLineWidth(0.5); doc.line(35, 737, width, 737); }
    });
  }

  // Presenta el documento PDF generado dentro del modal
  private _showPdfInModal(doc: jsPDF, template: TemplateRef<any>): void {
    const pdfBlob = doc.output('blob');
    if (this.pdfBlobUrl) URL.revokeObjectURL(this.pdfBlobUrl);
    this.pdfBlobUrl = URL.createObjectURL(pdfBlob);
    this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
    this.modalService.open(template, { size: 'xl', centered: true, backdrop: 'static' });
  }

  private filterBySearch(list: Request[]): Request[] {
    const q = this.searchQuery().trim().toLowerCase();
    if (!q) return list;
    return list.filter(r => {
      const names = (r.party?.names ?? '').toLowerCase();
      const lastNames = (r.party?.lastNames ?? '').toLowerCase();
      const rut = (r.party?.rut ?? '').toLowerCase();
      const plate = (r.vehicle?.plate ?? r.claim?.vehicle?.plate ?? r.benefit?.vehicle?.plate ?? '').toLowerCase();
      const folio = String(r.uuid ?? '').toLowerCase();
      return names.includes(q) || lastNames.includes(q) || rut.includes(q) || plate.includes(q) || folio.includes(q);
    });
  }

  private filterByEntities(list: Request[]): Request[] {
    const codes = this.selectedEntities() || [];
    if (codes.length === 0) return list;
    const selected = new Set(codes.map(c => Number(c)).filter(n => Number.isFinite(n)));
    return list.filter(r =>
      selected.has(Number(r.entityCode)) || selected.has(Number(r.targetEntityCode))
    );
  }

  private filterByProcess(list: Request[]): Request[] {
    const p = this.selectedProcess();
    return p ? list.filter(r => r.processSlug === p) : list;
  }

  private filterByStatus(list: Request[]): Request[] {
    const s = this.selectedStatus();
    if (!s || s === TERMINAL_FILTER) return list;
    return list.filter(r => r.statusSlug === s);
  }

  /** Cierre de solicitud: BpmService.isClosed o slugs terminales legacy. */
  private isTerminalRequest(r: Request): boolean {
    return this.bpmService.isClosed(r) || TERMINAL_STATUS_SLUGS.has(String(r.statusSlug ?? '').toUpperCase());
  }

  canReactivate(req: Request): boolean {
    return this.bpmService.isDiscarded(req);
  }

  /** Hard-delete definitivo: canDelete del menú historial + solo solicitudes desechadas. */
  canHardDelete(req: Request): boolean {
    const hasPerm = this.perm.canCurrent('canDelete') || this.perm.can('solicitudes/historial', 'canDelete');
    const slug = String(req?.statusSlug ?? '').toUpperCase();
    return hasPerm && slug === 'DISCARDED';
  }

  reactivate(req: Request): void {
    const uuid = req?.uuid;
    if (!uuid) return;
    Swal.fire({
      title: '¿Reactivar solicitud?',
      text: 'Volverá al paso inicial del flujo BPM para continuar.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({
        title: 'Reactivando...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });
      this.requestService.reactivateRequest(uuid).subscribe({
        next: (res) => {
          Swal.close();
          const step = String(res?.statusSlug || 'DRAFT').toUpperCase();
          Swal.fire({
            icon: 'success',
            title: 'Reactivada',
            text: `La solicitud volvió al paso inicial (${step}).`,
            timer: 1800,
            showConfirmButton: false
          });
          this.loadRequests();
        },
        error: (err) => {
          Swal.close();
          const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo reactivar.';
          Swal.fire('Error', msg, 'error');
        }
      });
    });
  }

  private getDeleteConfirmationText(req: Request): string {
    const proc = String(req?.processSlug ?? '').toUpperCase();
    if (proc === 'MEMBERSHIP_NEW') {
      return 'Acción de soporte: borra la incorporación completa (solicitud, socio y vehículo). No se puede deshacer.';
    }
    if (proc === 'VEHICLE_NEW') {
      return 'Acción de soporte: borra la solicitud y el vehículo descartado. El socio se conservará.';
    }
    return 'Acción de soporte: borra únicamente la solicitud y sus documentos. El socio y sus vehículos se conservan.';
  }

  hardDelete(req: Request): void {
    const uuid = req?.uuid;
    if (!uuid || !this.canHardDelete(req)) return;
    Swal.fire({
      title: '¿Eliminar definitivamente?',
      text: this.getDeleteConfirmationText(req),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) this._executeHardDelete(uuid);
    });
  }

  private _executeHardDelete(uuid: string): void {
    Swal.fire({ title: 'Eliminando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.requestService.deleteRequest(uuid).subscribe({
      next: () => {
        Swal.close();
        Swal.fire({ icon: 'success', title: 'Eliminada', text: 'Solicitud eliminada.', timer: 1800, showConfirmButton: false });
        this.loadRequests();
      },
      error: (err) => {
        Swal.close();
        const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo eliminar.';
        Swal.fire('Error', msg, 'error');
      }
    });
  }

  private filterByPeriod(list: Request[]): Request[] {
    const startId = Number(this.selectedPeriodStartId() ?? 0);
    const endId = Number(this.selectedPeriodEndId() ?? 0);
    if (!startId || !endId) return list;
    const startP = this.periods().find(p => Number(p.id) === startId);
    const endP = this.periods().find(p => Number(p.id) === endId);
    if (!startP || !endP) return list;
    const startVal = Number(startP.year) * 100 + Number(startP.period);
    const endVal = Number(endP.year) * 100 + Number(endP.period);
    return list.filter(r => this._matchesPeriodRange(r, startVal, endVal));
  }

  /**
   * Filtra por periodo del trámite (year/period o billingPeriod).
   * Desechada nueva ya sella periodo ACTIVE al desechar; sin periodo (legado) → createdAt.
   */
  private _matchesPeriodRange(r: Request, startVal: number, endVal: number): boolean {
    const slug = String(r.statusSlug || '').toUpperCase();
    const hasBilling =
      (r.year != null && r.period != null)
      || ((r as any).billingPeriod?.year != null && (r as any).billingPeriod?.period != null);

    if (slug === 'DISCARDED' && !hasBilling) {
      const created = this._localYearMonth(r.createdAt);
      if (!created) return true;
      if (created >= startVal && created <= endVal) return true;
      return created > endVal;
    }

    const value = this._requestPeriodValue(r);
    if (!value) return false;
    return value >= startVal && value <= endVal;
  }

  private _requestPeriodValue(r: Request): number {
    if (r.year != null && r.period != null) {
      return Number(r.year) * 100 + Number(r.period);
    }
    const billed = (r as any).billingPeriod;
    if (billed?.year != null && billed?.period != null) {
      return Number(billed.year) * 100 + Number(billed.period);
    }
    const reimb = r.reimbursements?.[0];
    if (reimb?.year != null && reimb?.period != null) {
      return Number(reimb.year) * 100 + Number(reimb.period);
    }
    if (reimb?.periodId != null) {
      const period = this.periods().find(p => Number(p.id) === Number(reimb.periodId));
      if (period) return Number(period.year) * 100 + Number(period.period);
    }
    const loss = r.claim?.details?.[0]?.dateOfLoss;
    if (loss) return this._localYearMonth(loss);
    return this._localYearMonth(r.createdAt);
  }

  private _localYearMonth(raw: string | Date | null | undefined): number {
    const d = new Date(raw as any);
    if (!Number.isFinite(d.getTime())) return 0;
    return d.getFullYear() * 100 + (d.getMonth() + 1);
  }

  /** Periodo contable del trámite (si ya se registró); no aplica a cobro mensual previa sin cierre. */
  billingPeriodLabel(req: Request): string | null {
    const year = req.year ?? (req as any).billingPeriod?.year;
    const period = req.period ?? (req as any).billingPeriod?.period;
    if (year == null || period == null) return null;
    return `${String(period).padStart(2, '0')}/${year}`;
  }

  getEntityName(code: number | string): string {
    if (!code) return 'N/A';
    const entity = this.entities().find(e => e.code == code);
    return entity ? `${entity.code} - ${entity.name}` : `Entidad ${code}`;
  }

  getProcessName(slug: string): string {
    if (!slug) return 'Proceso';
    const process = this.processes().find(p => p.slug === slug || p.code === slug);
    return process?.name ?? `Proceso ${slug}`;
  }

  currentEntityCode(): number | null {
    const code = (this.authService.sharingEntity() as any)?.code;
    return code == null || code === '' ? null : Number(code);
  }

  isIncomingTransfer(req: Request): boolean {
    if (req.processSlug !== 'TRANSFER' || req.targetEntityCode == null) return false;
    const me = this.currentEntityCode();
    if (me == null) return false;
    return Number(req.targetEntityCode) === me && Number(req.entityCode) !== me;
  }

  isOutgoingTransfer(req: Request): boolean {
    if (req.processSlug !== 'TRANSFER' || req.targetEntityCode == null) return false;
    const me = this.currentEntityCode();
    if (me == null) return false;
    return Number(req.entityCode) === me && Number(req.targetEntityCode) !== me;
  }

  transferReception(req: Request): { actor: string; at: string | null } | null {
    if (req.processSlug !== 'TRANSFER' || req.statusSlug !== 'APPROVED') return null;
    const step = (req.flowHistory || []).find(s => s.toStatusSlug === 'APPROVED' && s.status !== 'REJECTED');
    const actor = step?.actorLabel || 'Entidad destino';
    const raw = step?.decidedAt || req.updatedAt || null;
    const at = raw ? new Date(raw).toLocaleString('es-CL') : null;
    return { actor, at };
  }


  downloadPreviewPdf(): void {
    if (!this.pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = this.pdfBlobUrl;
    a.download = 'Informe_solicitud.pdf';
    a.click();
  }

  private openSimplePreview(req: Request, template: TemplateRef<any>): void {
    const doc = new jsPDF('p', 'mm', 'letter');
    const entityName = this.getEntityName(req.entityCode ?? '');
    let y = this._addHeader(doc, entityName, 'INFORME DE SOLICITUD');

    const statusName = req.statusObj?.name || this.catalogService.getStatusName(req.statusSlug);
    const created = req.createdAt ? new Date(req.createdAt).toLocaleString('es-CL') : 'N/A';
    const body: string[][] = [
      ['Folio', req.uuid ?? 'N/A'],
      ['Proceso', this.getProcessName(req.processSlug)],
      ['Estado', statusName],
      ['Fecha de creación', created],
      ['Entidad', entityName]
    ];
    const billing = this.billingPeriodLabel(req);
    if (billing) body.push(['Periodo de facturación', billing]);

    if (req.party) {
      body.push(
        ['Socio', `${req.party.names ?? ''} ${req.party.lastNames ?? ''}`.trim() || 'N/A'],
        ['RUT', req.party.rut ?? 'N/A']
      );
    }

    const plate = req.vehicle?.plate ?? req.claim?.vehicle?.plate ?? req.benefit?.vehicle?.plate;
    if (plate) body.push(['Patente', plate]);

    if (req.processSlug === 'PAYMENT' && (req.payment || req.amount != null)) {
      const amount = new Intl.NumberFormat('es-CL').format(req.payment?.amount ?? req.amount ?? 0);
      const year = req.payment?.year ?? req.year;
      const period = req.payment?.period ?? req.period;
      body.push(['Monto', `$${amount}`], ['Periodo de pago', `${year ?? ''} - ${String(period ?? '').padStart(2, '0')}`]);
    }

    if (req.processSlug === 'TRANSFER') {
      const origin = this.getEntityName(req.entityCode ?? req.transfer?.originEntityCode ?? '');
      const dest = this.getEntityName(req.targetEntityCode ?? req.transfer?.targetEntityCode ?? '');
      body.push(['Entidad origen', origin], ['Entidad destino', dest]);
      if (this.bpmService.documentGloss(req)) {
        body.push(['Acta / voto', this.bpmService.documentGloss(req)]);
      }
      const reception = this.transferReception(req);
      if (reception) {
        body.push(['Recepción', `Aceptada por ${reception.actor}${reception.at ? ' el ' + reception.at : ''}`]);
      } else if (req.statusSlug === 'PENDING') {
        body.push(['Recepción', 'Pendiente de aceptación en destino']);
      }
    }

    if ((req.processSlug === 'BENEFIT' || req.processSlug === 'BENEFITS') && req.benefit) {
      const typeLabel = BENEFIT_TYPE_LABELS[req.benefit.type] || req.benefit.type;
      const amount = new Intl.NumberFormat('es-CL').format(req.amount ?? req.benefit?.amount ?? 0);
      body.push(['Tipo de beneficio', typeLabel], ['Monto', `$${amount}`]);
    }

    autoTable(doc, {
      startY: y,
      head: [['DETALLE', '']],
      body,
      theme: 'grid',
      headStyles: { fillColor: [52, 152, 219] }
    });

    this._showPdfInModal(doc, template);
  }

  private _addHeader(doc: jsPDF, entity: string, title: string): number {
    doc.setFontSize(14).setFont('helvetica', 'bold').text('Fondo de Protección Vehicular', 20, 20);
    doc.setFontSize(10).setFont('helvetica', 'italic').text(entity || 'N/A', 20, 26);
    doc.setFontSize(12).setFont('helvetica', 'bold').text(title, 20, 35);
    return 42;
  }
}

